import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase/config';
import { 
  FaTruck, 
  FaClock, 
  FaCheckCircle, 
  FaBox,
  FaCrown,
  FaArrowRight,
  FaUserCircle,
  FaEnvelope,
  FaCalendarAlt,
  FaUser
} from 'react-icons/fa';
import './Profil.css';

const Profil = () => {
  const { currentUser } = useAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [stats, setStats] = useState({
    totalRdvs: 0,
    enAttente: 0,
    confirmes: 0,
    termines: 0,
    pointsFidelite: 0
  });

  // Charger les informations de l'utilisateur
  useEffect(() => {
    if (!currentUser) return;

    const userRef = ref(database, `utilisateurs/${currentUser.uid}`);
    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setUserInfo(data);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Charger les statistiques des rendez-vous
  useEffect(() => {
    if (!currentUser) return;

    const userRdvsRef = ref(database, `rendezvous/${currentUser.uid}`);
    onValue(userRdvsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const rdvs = Object.values(data);
        setStats({
          totalRdvs: rdvs.length,
          enAttente: rdvs.filter(r => r.statut === 'en attente').length,
          confirmes: rdvs.filter(r => r.statut === 'confirmé').length,
          termines: rdvs.filter(r => r.statut === 'terminé').length,
          pointsFidelite: rdvs.length * 10
        });
      }
    });
  }, [currentUser]);

  // Obtenir le nom complet de l'utilisateur
  const getFullName = () => {
    if (userInfo?.prenom && userInfo?.nom) {
      return `${userInfo.prenom} ${userInfo.nom}`;
    }
    if (userInfo?.prenom) {
      return userInfo.prenom;
    }
    if (userInfo?.nomComplet) {
      return userInfo.nomComplet;
    }
    return currentUser?.email?.split('@')[0] || 'Client';
  };

  // Obtenir les initiales du nom complet
  const getInitials = () => {
    const fullName = getFullName();
    if (fullName && fullName !== currentUser?.email?.split('@')[0]) {
      const parts = fullName.split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return fullName.charAt(0).toUpperCase();
    }
    return currentUser?.email?.charAt(0).toUpperCase() || '?';
  };

  const memberSince = currentUser?.metadata?.creationTime 
    ? new Date(currentUser.metadata.creationTime).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'Non disponible';

  return (
    <div className="profil-modern">
      {/* Header */}
      <div className="profil-header">
        <div className="header-overlay"></div>
        <div className="header-content">
          <div className="header-left">
            <h1>Mon espace personnel</h1>
            <p>Gérez vos déménagements et suivez vos activités</p>
          </div>
          <div className="header-right">
            <div className="points-badge">
              <FaCrown className="crown-icon" />
              <span>{stats.pointsFidelite} points fidélité</span>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="profil-content">
        {/* Sidebar - Profil */}
        <div className="profil-sidebar">
          <div className="profile-card">
            <div className="avatar-container">
              <div className="avatar-large">
                <span className="avatar-initials">{getInitials()}</span>
              </div>
            </div>
            
            {/* Affichage du nom et prénom */}
            <h2 className="user-name">{getFullName()}</h2>
            <p className="user-email-display">{currentUser?.email}</p>
            
            <div className="user-badge">
              <FaCheckCircle className="verified-icon" />
              <span>Compte vérifié</span>
            </div>

            <div className="user-info-list">
              <div className="info-row">
                <FaUser className="info-icon" />
                <div>
                  <span className="info-label">Nom complet : </span>
                  <span className="info-value">{getFullName()}</span>
                </div>
              </div>

              <div className="info-row">
                <FaEnvelope className="info-icon" />
                <div>
                  <span className="info-label">Email : </span>
                  <span className="info-value">{currentUser?.email}</span>
                </div>
              </div>
              
              <div className="info-row">
                <FaCalendarAlt className="info-icon" />
                <div>
                  <span className="info-label">Membre depuis : </span>
                  <span className="info-value">{memberSince}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="profil-main">
          {/* Statistiques */}
          <div className="stats-grid-modern">
            <div className="stat-card-modern total">
              <div className="stat-icon-wrapper">
                <FaTruck />
              </div>
              <div className="stat-details">
                <span className="stat-number-modern">{stats.totalRdvs}</span>
                <span className="stat-label-modern">Total</span>
              </div>
            </div>

            <div className="stat-card-modern pending">
              <div className="stat-icon-wrapper">
                <FaClock />
              </div>
              <div className="stat-details">
                <span className="stat-number-modern">{stats.enAttente}</span>
                <span className="stat-label-modern">En attente</span>
              </div>
            </div>

            <div className="stat-card-modern confirmed">
              <div className="stat-icon-wrapper">
                <FaCheckCircle />
              </div>
              <div className="stat-details">
                <span className="stat-number-modern">{stats.confirmes}</span>
                <span className="stat-label-modern">Confirmés</span>
              </div>
            </div>

            <div className="stat-card-modern completed">
              <div className="stat-icon-wrapper">
                <FaBox />
              </div>
              <div className="stat-details">
                <span className="stat-number-modern">{stats.termines}</span>
                <span className="stat-label-modern">Terminés</span>
              </div>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="quick-actions-modern">
            <h3>Actions rapides</h3>
            <div className="actions-grid">
              <Link to="/dashboard/rendezvous" className="action-card">
                <div className="action-icon blue">
                  <FaTruck />
                </div>
                <div className="action-info">
                  <h4>Nouveau rendez-vous</h4>
                  <p>Planifiez votre prochain déménagement</p>
                </div>
                <FaArrowRight className="action-arrow" />
              </Link>

              <Link to="/dashboard/planning" className="action-card">
                <div className="action-icon green">
                  <FaClock />
                </div>
                <div className="action-info">
                  <h4>Mon planning</h4>
                  <p>Consultez vos rendez-vous à venir</p>
                </div>
                <FaArrowRight className="action-arrow" />
              </Link>

              <Link to="/" className="action-card">
                <div className="action-icon purple">
                  <FaUserCircle />
                </div>
                <div className="action-info">
                  <h4>Retour à l'accueil</h4>
                  <p>Découvrez nos services</p>
                </div>
                <FaArrowRight className="action-arrow" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profil;